import { useState, useEffect, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Sun, Plus, ArrowLeft, List, Edit, Trash2, Filter, Save, Link2
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { KanbanBoard } from "@/components/solar/kanban/KanbanBoard";
import { CardList } from "@/components/solar/kanban/CardList";
import { CardForm } from "@/components/solar/kanban/CardForm";
import { ProjetoFormDialog } from "@/components/solar/kanban/ProjetoFormDialog";
import { FluxoCard, KanbanColumn, Funil } from "@/components/solar/kanban/types";
import { useNavigate } from "react-router-dom";
import { FunilDialog } from "@/components/solar/kanban/FunilDialog";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";

const FluxoSolar = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState<string>("kanban");
  
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [currentCard, setCurrentCard] = useState<FluxoCard | null>(null);
  const [newCard, setNewCard] = useState<Partial<FluxoCard>>({
    titulo: "",
    descricao: "",
    status: "",
    prioridade: "media",
    cor: "bg-card",
    perspective: false
  });
  const [showProjetoForm, setShowProjetoForm] = useState<boolean>(false);
  
  const [funis, setFunis] = useState<Funil[]>([
    {
      id: "default",
      nome: "Funil Padrão",
      isDefault: true,
      colunas: [
        { id: "lead", titulo: "Leads", cards: [], cor: "bg-blue-50" },
        { id: "contato", titulo: "Em Contato", cards: [], cor: "bg-purple-50" },
        { id: "proposta", titulo: "Proposta Enviada", cards: [], cor: "bg-yellow-50" },
        { id: "negociacao", titulo: "Em Negociação", cards: [], cor: "bg-orange-50" },
        { id: "fechado", titulo: "Fechado", cards: [], cor: "bg-green-50" },
        { id: "perdido", titulo: "Perdido", cards: [], cor: "bg-red-50" },
      ],
      icone: "Filter"
    }
  ]);
  
  const [funilAtual, setFunilAtual] = useState<string>("default");
  const [columns, setColumns] = useState<KanbanColumn[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [funilDialogOpen, setFunilDialogOpen] = useState(false);
  const [currentFunil, setCurrentFunil] = useState<Funil | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [funilToDelete, setFunilToDelete] = useState<string | null>(null);
  const [dataSaved, setDataSaved] = useState(true);
  const [showShareForm, setShowShareForm] = useState(false);
  const [formLink, setFormLink] = useState('');
  const [autoSaveTimer, setAutoSaveTimer] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const loadFluxoData = () => {
      setIsLoading(true);
      try {
        const savedFunis = localStorage.getItem("funisProjeto");
        if (savedFunis) {
          try {
            const parsedFunis = JSON.parse(savedFunis);
            
            const funisWithDates = parsedFunis.map((funil: any) => ({
              ...funil,
              colunas: funil.colunas.map((col: any) => ({
                ...col,
                cards: col.cards.map((card: any) => ({
                  ...card,
                  criadoEm: new Date(card.criadoEm),
                  projetoCompleto: card.projetoCompleto ? {
                    ...card.projetoCompleto,
                    criadoEm: new Date(card.projetoCompleto.criadoEm),
                    atualizadoEm: new Date(card.projetoCompleto.atualizadoEm),
                  } : undefined
                }))
              }))
            }));
            
            setFunis(funisWithDates);
            
            const lastUsedFunil = localStorage.getItem("funilAtual") || funisWithDates[0]?.id || "default";
            setFunilAtual(lastUsedFunil);
          } catch (error) {
            console.error("Erro ao carregar funis de projeto:", error);
          }
        } else {
          const savedFluxo = localStorage.getItem("fluxoSolar");
          if (savedFluxo) {
            try {
              const parsedFluxo = JSON.parse(savedFluxo);
              const columnsWithDates = parsedFluxo.map((col: any) => ({
                ...col,
                cards: col.cards.map((card: any) => ({
                  ...card,
                  criadoEm: new Date(card.criadoEm),
                  projetoCompleto: card.projetoCompleto ? {
                    ...card.projetoCompleto,
                    criadoEm: new Date(card.projetoCompleto.criadoEm),
                    atualizadoEm: new Date(card.projetoCompleto.atualizadoEm),
                  } : undefined
                }))
              }));
              
              const defaultFunil: Funil = {
                id: "default",
                nome: "Funil Padrão",
                isDefault: true,
                colunas: columnsWithDates,
                icone: "Filter"
              };
              
              setFunis([defaultFunil]);
              setFunilAtual("default");
            } catch (error) {
              console.error("Erro ao carregar fluxo de projeto:", error);
            }
          }
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    loadFluxoData();
  }, []);

  useEffect(() => {
    if (!isLoading) {
      const funilSelecionado = funis.find(f => f.id === funilAtual);
      if (funilSelecionado) {
        setColumns(funilSelecionado.colunas);
        localStorage.setItem("funilAtual", funilAtual);
      }
    }
  }, [funilAtual, funis, isLoading]);

  useEffect(() => {
    if (columns.length > 0 && newCard.status === "") {
      setNewCard(prev => ({
        ...prev,
        status: columns[0].id
      }));
    }
  }, [columns, newCard.status]);

  useEffect(() => {
    if (!dataSaved && !isLoading) {
      if (autoSaveTimer) {
        clearTimeout(autoSaveTimer);
      }
      
      const timer = setTimeout(() => {
        saveData();
      }, 3000);
      
      setAutoSaveTimer(timer);
    }
    
    return () => {
      if (autoSaveTimer) {
        clearTimeout(autoSaveTimer);
      }
    };
  }, [dataSaved, columns]);

  const saveData = useCallback(() => {
    if (isLoading) return;
    
    const updatedFunis = funis.map(f => {
      if (f.id === funilAtual) {
        return {
          ...f,
          colunas: columns
        };
      }
      return f;
    });
    
    try {
      localStorage.setItem("funisProjeto", JSON.stringify(updatedFunis));
      setFunis(updatedFunis);
      setDataSaved(true);
      return true;
    } catch (error) {
      console.error("Erro ao salvar dados:", error);
      return false;
    }
  }, [funis, funilAtual, columns, isLoading]);

  const handleSaveData = () => {
    if (saveData()) {
      toast({
        title: "Dados salvos",
        description: "Todas as alterações foram salvas com sucesso."
      });
    } else {
      toast({
        title: "Erro ao salvar",
        description: "Ocorreu um erro ao salvar os dados.",
        variant: "destructive"
      });
    }
  };

  const handleAddFunil = () => {
    setCurrentFunil(null);
    setFunilDialogOpen(true);
  };

  const handleEditFunil = (funil: Funil) => {
    setCurrentFunil(JSON.parse(JSON.stringify(funil)));
    setFunilDialogOpen(true);
  };

  const handleDeleteFunil = (funilId: string) => {
    const funilToDelete = funis.find(f => f.id === funilId);
    if (funilToDelete?.isDefault) {
      toast({
        title: "Não é possível excluir",
        description: "O funil padrão não pode ser excluído.",
        variant: "destructive",
      });
      return;
    }
    
    setFunilToDelete(funilId);
    setConfirmDeleteOpen(true);
  };

  const confirmDeleteFunil = () => {
    if (funilToDelete) {
      const updatedFunis = funis.filter(f => f.id !== funilToDelete);
      setFunis(updatedFunis);
      
      localStorage.setItem("funisProjeto", JSON.stringify(updatedFunis));
      
      if (funilAtual === funilToDelete) {
        const defaultFunil = updatedFunis.find(f => f.isDefault) || updatedFunis[0];
        if (defaultFunil) {
          setFunilAtual(defaultFunil.id);
        }
      }
      
      toast({
        title: "Funil excluído",
        description: "O funil foi excluído com sucesso.",
      });
    }
    
    setConfirmDeleteOpen(false);
    setFunilToDelete(null);
  };

  const handleSaveFunil = (funilData: Partial<Funil>) => {
    if (currentFunil) {
      const updatedFunis = funis.map(f => {
        if (f.id === currentFunil.id) {
          return {
            ...f,
            nome: funilData.nome || f.nome,
            icone: funilData.icone || f.icone,
            colunas: funilData.colunas || f.colunas
          };
        }
        return f;
      });
      
      setFunis(updatedFunis);
      localStorage.setItem("funisProjeto", JSON.stringify(updatedFunis));
      
      toast({
        title: "Funil atualizado",
        description: `O funil "${funilData.nome}" foi atualizado.`,
      });
    } else {
      const newFunil: Funil = {
        id: uuidv4(),
        nome: funilData.nome || "Novo Funil",
        isDefault: false,
        icone: funilData.icone || "Filter",
        colunas: funilData.colunas || [
          { id: "lead", titulo: "Leads", cards: [], cor: "bg-blue-50" },
          { id: "contato", titulo: "Em Contato", cards: [], cor: "bg-purple-50" },
          { id: "proposta", titulo: "Proposta Enviada", cards: [], cor: "bg-yellow-50" },
          { id: "negociacao", titulo: "Em Negociação", cards: [], cor: "bg-orange-50" },
          { id: "fechado", titulo: "Fechado", cards: [], cor: "bg-green-50" },
          { id: "perdido", titulo: "Perdido", cards: [], cor: "bg-red-50" },
        ]
      };
      
      const updatedFunis = [...funis, newFunil];
      setFunis(updatedFunis);
      setFunilAtual(newFunil.id);
      
      localStorage.setItem("funisProjeto", JSON.stringify(updatedFunis));
      
      toast({
        title: "Funil criado",
        description: `O funil "${newFunil.nome}" foi criado.`,
      });
    }
    
    setFunilDialogOpen(false);
    setCurrentFunil(null);
  };

  const handleAddCard = () => {
    if (!newCard.titulo) {
      toast({
        title: "Título obrigatório",
        description: "Por favor, informe um título para o card.",
        variant: "destructive"
      });
      return;
    }

    const card: FluxoCard = {
      id: `card-${Date.now()}`,
      titulo: newCard.titulo || "",
      descricao: newCard.descricao || "",
      status: newCard.status || (columns.length > 0 ? columns[0].id : "lead"),
      prioridade: newCard.prioridade || "media",
      cliente: newCard.cliente,
      telefone: newCard.telefone,
      email: newCard.email,
      criadoEm: new Date(),
      cor: newCard.cor || "bg-card",
      perspective: newCard.perspective || false,
      checklistPreenchido: {
        integrador: false,
        unidadeConsumidora: false,
        eletricos: false,
        mecanicos: false,
        financeiros: false,
        documentos: false,
      }
    };

    const updatedColumns = columns.map((col) => {
      if (col.id === card.status) {
        return {
          ...col,
          cards: [...col.cards, card],
        };
      }
      return col;
    });

    setColumns(updatedColumns);

    setNewCard({
      titulo: "",
      descricao: "",
      status: columns.length > 0 ? columns[0].id : "lead",
      prioridade: "media",
      cor: "bg-card",
      perspective: false
    });
    
    setDialogOpen(false);
    setDataSaved(false);

    toast({
      title: "Novo card adicionado",
      description: `O card "${card.titulo}" foi adicionado à coluna ${
        columns.find((col) => col.id === card.status)?.titulo || ""
      }.`,
    });
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    const { source, destination } = result;
    
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    const sourceColumn = columns.find(col => col.id === source.droppableId);
    const destColumn = columns.find(col => col.id === destination.droppableId);
    
    if (!sourceColumn || !destColumn) return;

    const newColumns = [...columns];
    
    const sourceColumnIndex = newColumns.findIndex(col => col.id === source.droppableId);
    const destColumnIndex = newColumns.findIndex(col => col.id === destination.droppableId);
    
    const cardToMove = {...sourceColumn.cards[source.index]};
    
    cardToMove.status = destination.droppableId;
    
    newColumns[sourceColumnIndex].cards.splice(source.index, 1);
    
    newColumns[destColumnIndex].cards.splice(destination.index, 0, cardToMove);
    
    setColumns(newColumns);
    setDataSaved(false);
    
    setTimeout(() => {
      saveData();
      
      toast({
        title: "Card movido",
        description: `O card "${cardToMove.titulo}" foi movido para ${destColumn.titulo}.`,
      });
    }, 100);
  };

  const handleEditClick = (card: FluxoCard) => {
    setCurrentCard(JSON.parse(JSON.stringify(card)));
    setDialogOpen(true);
  };

  const handleViewProjeto = (card: FluxoCard) => {
    setCurrentCard(JSON.parse(JSON.stringify(card)));
    setTimeout(() => {
      setShowProjetoForm(true);
    }, 100);
  };

  const handleSaveProjeto = (projetoData: any) => {
    if (!currentCard) return;

    const updatedCard = {
      ...currentCard,
      projetoCompleto: {
        ...currentCard.projetoCompleto,
        id: currentCard.projetoCompleto?.id || uuidv4(),
        titulo: currentCard.titulo,
        descricao: currentCard.descricao,
        status: currentCard.status,
        prioridade: currentCard.prioridade,
        criadoEm: currentCard.projetoCompleto?.criadoEm || new Date(),
        atualizadoEm: new Date(),
        criadoPor: "current-user-id",
        ...projetoData
      },
      checklistPreenchido: {
        integrador: !!projetoData.dadosIntegrador?.regional,
        unidadeConsumidora: !!projetoData.dadosUnidadeConsumidora?.titular,
        eletricos: !!projetoData.dadosEletricos?.classe,
        mecanicos: !!projetoData.dadosMecanicos?.telhado,
        financeiros: !!projetoData.dadosFinanceiros?.formaPagamento,
        documentos: (projetoData.documentos?.length || 0) > 0,
      }
    };

    setColumns(
      columns.map((col) => ({
        ...col,
        cards: col.cards.map((c) => (c.id === currentCard.id ? updatedCard : c)),
      }))
    );
    setDataSaved(false);

    setTimeout(() => {
      setShowProjetoForm(false);
      setTimeout(() => {
        setCurrentCard(null);
      }, 100);
    }, 100);
    
    toast({
      title: "Projeto salvo",
      description: `O projeto "${updatedCard.titulo}" foi atualizado com sucesso.`,
    });
  };

  const handleUpdateCard = () => {
    if (!currentCard) return;
    
    if (!currentCard.titulo) {
      toast({
        title: "Título obrigatório",
        description: "Por favor, informe um título para o card.",
        variant: "destructive"
      });
      return;
    }

    setColumns(
      columns.map((col) => ({
        ...col,
        cards: col.cards.map((c) => (c.id === currentCard.id ? currentCard : c)),
      }))
    );
    setDataSaved(false);

    setDialogOpen(false);
    setTimeout(() => {
      setCurrentCard(null);
    }, 100);

    toast({
      title: "Card atualizado",
      description: `O card "${currentCard.titulo}" foi atualizado com sucesso.`,
    });
  };

  const handleDeleteCard = () => {
    if (!currentCard) return;
    
    if (confirm("Tem certeza que deseja remover este card?")) {
      setColumns(
        columns.map((col) => ({
          ...col,
          cards: col.cards.filter((c) => c.id !== currentCard.id),
        }))
      );
      setDataSaved(false);

      setDialogOpen(false);
      setTimeout(() => {
        setCurrentCard(null);
      }, 100);

      toast({
        title: "Card removido",
        description: "O card foi removido permanentemente.",
        variant: "destructive",
      });
    }
  };

  const handleCardFormChange = (field: string, value: any) => {
    if (currentCard) {
      setCurrentCard({
        ...currentCard,
        [field]: value
      });
    } else {
      setNewCard({
        ...newCard,
        [field]: value
      });
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleShareForm = () => {
    const baseUrl = window.location.origin;
    const linkToShare = `${baseUrl}/form/card/${funilAtual}`;
    setFormLink(linkToShare);
    setShowShareForm(true);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(formLink).then(() => {
      toast({
        title: "Link copiado",
        description: "O link do formulário foi copiado para a área de transferência."
      });
    });
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="container mx-auto p-4 md:p-6">
          <div className="flex justify-center items-center h-[60vh]">
            <p className="text-lg text-muted-foreground">Carregando fluxo de projetos...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto p-4 md:p-6 max-w-[1600px]">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Sun className="h-7 w-7 text-yellow-500" />
              Fluxo de Projetos
            </h1>
            <p className="text-muted-foreground text-sm">Gerencie o fluxo de trabalho dos projetos solares</p>
          </div>
          
          <div className="flex flex-wrap gap-2 mt-4 md:mt-0">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate("/solar")} 
              className="flex items-center gap-1 h-8"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Voltar
            </Button>
            
            <Button
              variant={dataSaved ? "outline" : "default"}
              size="sm"
              onClick={handleSaveData}
              className="flex items-center gap-1 h-8"
              disabled={dataSaved}
            >
              <Save className="h-3.5 w-3.5" />
              {dataSaved ? "Dados Salvos" : "Salvar"}
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleShareForm}
              className="flex items-center gap-1 h-8"
            >
              <Link2 className="h-3.5 w-3.5" />
              Compartilhar
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="flex items-center gap-1 h-8">
                  <List className="h-3.5 w-3.5" />
                  {funis.find(f => f.id === funilAtual)?.nome || "Funil"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Funis de Projeto</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {funis.map(funil => (
                  <DropdownMenuItem 
                    key={funil.id}
                    className="flex justify-between items-center cursor-pointer"
                    onClick={() => setFunilAtual(funil.id)}
                  >
                    <span className="flex-1">{funil.nome} {funil.isDefault && "(Padrão)"}</span>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditFunil(funil);
                        }}
                      >
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      {!funil.isDefault && (
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-6 w-6 text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFunil(funil.id);
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  className="flex items-center cursor-pointer"
                  onClick={handleAddFunil}
                >
                  <Plus className="h-3.5 w-3.5 mr-2" /> Novo Funil
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        
        <div className="flex justify-between items-center mb-3">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full max-w-[200px]">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="kanban" className="text-xs">Kanban</TabsTrigger>
              <TabsTrigger value="lista" className="text-xs">Lista</TabsTrigger>
            </TabsList>
          </Tabs>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <Button 
              onClick={() => {
                setCurrentCard(null);
                setDialogOpen(true);
              }}
              size="sm"
              className="h-8"
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              Novo Card
            </Button>
          </Dialog>
        </div>
        
        <Card className="border shadow-sm">
          <CardContent className="p-3">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsContent value="kanban" className="mt-0">
                <KanbanBoard 
                  columns={columns}
                  onDragEnd={handleDragEnd}
                  onEditClick={handleEditClick}
                  onViewProjeto={handleViewProjeto}
                  formatDate={formatDate}
                />
              </TabsContent>
              
              <TabsContent value="lista" className="mt-0">
                <CardList 
                  columns={columns}
                  onEditClick={handleEditClick}
                  onViewProjeto={handleViewProjeto}
                  formatDate={formatDate}
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
        
        {dialogOpen && (
          <Dialog 
            open={dialogOpen} 
            onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) {
                setTimeout(() => {
                  setCurrentCard(null);
                }, 100);
              }
            }}
          >
            <CardForm 
              card={currentCard || newCard}
              onChange={handleCardFormChange}
              onSave={currentCard ? handleUpdateCard : handleAddCard}
              isEditing={!!currentCard}
              onDelete={currentCard ? handleDeleteCard : undefined}
              columns={columns}
            />
          </Dialog>
        )}
        
        {currentCard && showProjetoForm && (
          <Dialog 
            open={showProjetoForm} 
            onOpenChange={(open) => {
              if (!open) {
                setShowProjetoForm(false);
                setTimeout(() => setCurrentCard(null), 100);
              }
            }}
          >
            <ProjetoFormDialog 
              card={currentCard}
              onSave={handleSaveProjeto}
              onCancel={() => {
                setShowProjetoForm(false);
                setTimeout(() => setCurrentCard(null), 100);
              }}
            />
          </Dialog>
        )}
        
        <FunilDialog 
          open={funilDialogOpen}
          onOpenChange={setFunilDialogOpen}
          funil={currentFunil}
          onSave={handleSaveFunil}
          onCancel={() => {
            setFunilDialogOpen(false);
            setCurrentFunil(null);
          }}
        />
        
        <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Tem certeza?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta ação não pode ser desfeita. Isso excluirá permanentemente o funil e todos os seus cards.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setFunilToDelete(null)}>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDeleteFunil} className="bg-destructive text-destructive-foreground">
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        
        {showShareForm && (
          <Dialog 
            open={showShareForm} 
            onOpenChange={setShowShareForm}
          >
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Compartilhar Formulário</DialogTitle>
                <DialogDescription>
                  Compartilhe este link para que pessoas externas possam adicionar cards ao funil.
                </DialogDescription>
              </DialogHeader>
              <div className="flex items-center space-x-2 mt-2">
                <Input 
                  value={formLink} 
                  readOnly 
                  className="flex-1"
                />
                <Button type="submit" onClick={copyToClipboard}>
                  Copiar
                </Button>
              </div>
              <DialogFooter className="mt-4">
                <Button variant="outline" onClick={() => setShowShareForm(false)}>
                  Fechar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Layout>
  );
};

export default FluxoSolar;
